import { $createCodeNode } from '@lexical/code';
import {
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from '@lexical/list';
import { $isDecoratorBlockNode } from '@lexical/react/LexicalDecoratorBlockNode';
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  $isQuoteNode,
  HeadingTagType,
} from '@lexical/rich-text';
import { $isAtNodeEnd, $setBlocksType } from '@lexical/selection';
import { $isTableSelection } from '@lexical/table';
import { $getNearestBlockElementAncestorOrThrow } from '@lexical/utils';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  ElementNode,
  LexicalEditor,
  RangeSelection,
  TextNode,
} from 'lexical';
import { debounce } from 'lodash-es';
import { useMemo, useRef } from 'react';

export function currency(amount) {
  return (amount / 100).toFixed(2);
}

export function getSelectedNode(selection: RangeSelection): TextNode | ElementNode {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  } else {
    return $isAtNodeEnd(anchor) ? anchorNode : focusNode;
  }
}

export const formatParagraph = (editor: LexicalEditor) => {
  editor.update(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      $setBlocksType(selection, () => $createParagraphNode());
    }
  });
};

export const formatHeading = (
  editor: LexicalEditor,
  blockType: string,
  headingSize: HeadingTagType
) => {
  if (blockType !== headingSize) {
    editor.update(() => {
      const selection = $getSelection();
      $setBlocksType(selection, () => $createHeadingNode(headingSize));
    });
  }
};

export const formatBulletList = (editor: LexicalEditor, blockType: string) => {
  if (blockType !== 'bullet') {
    editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
  } else {
    formatParagraph(editor);
  }
};

export const formatCheckList = (editor: LexicalEditor, blockType: string) => {
  if (blockType !== 'check') {
    editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
  } else {
    formatParagraph(editor);
  }
};

export const formatNumberedList = (editor: LexicalEditor, blockType: string) => {
  if (blockType !== 'number') {
    editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
  } else {
    formatParagraph(editor);
  }
};

export const formatQuote = (editor: LexicalEditor, blockType: string) => {
  if (blockType !== 'quote') {
    editor.update(() => {
      const selection = $getSelection();
      $setBlocksType(selection, () => $createQuoteNode());
    });
  }
};

export const formatCode = (editor: LexicalEditor, blockType: string) => {
  if (blockType !== 'code') {
    editor.update(() => {
      let selection = $getSelection();

      if (selection !== null) {
        if (selection.isCollapsed()) {
          $setBlocksType(selection, () => $createCodeNode());
        } else {
          const textContent = selection.getTextContent();
          const codeNode = $createCodeNode();
          selection.insertNodes([codeNode]);
          selection = $getSelection();
          if ($isRangeSelection(selection)) {
            selection.insertRawText(textContent);
          }
        }
      }
    });
  }
};

export const clearFormatting = (editor: LexicalEditor) => {
  editor.update(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection) || $isTableSelection(selection)) {
      const anchor = selection.anchor;
      const focus = selection.focus;
      const nodes = selection.getNodes();
      const extractedNodes = selection.extract();

      if (anchor.key === focus.key && anchor.offset === focus.offset) {
        return;
      }

      nodes.forEach((node, idx) => {
        // We split the first and last node by the selection
        // So that we don't format unselected text inside those nodes
        if ($isTextNode(node)) {
          // Use a separate variable to ensure TS does not lose the refinement
          let textNode = node;
          if (idx === 0 && anchor.offset !== 0) {
            textNode = textNode.splitText(anchor.offset)[1] || textNode;
          }
          if (idx === nodes.length - 1) {
            textNode = textNode.splitText(focus.offset)[0] || textNode;
          }
          /**
           * If the selected text has one format applied
           * selecting a portion of the text, could
           * clear the format to the wrong portion of the text.
           *
           * The cleared text is based on the length of the selected text.
           */
          // We need this in case the selected text only has one format
          const extractedTextNode = extractedNodes[0];
          if (nodes.length === 1 && $isTextNode(extractedTextNode)) {
            textNode = extractedTextNode;
          }

          if (textNode.__style !== '') {
            textNode.setStyle('');
          }
          if (textNode.__format !== 0) {
            textNode.setFormat(0);
            $getNearestBlockElementAncestorOrThrow(textNode).setFormat('');
          }
          node = textNode;
        } else if ($isHeadingNode(node) || $isQuoteNode(node)) {
          node.replace($createParagraphNode(), true);
        } else if ($isDecoratorBlockNode(node)) {
          node.setFormat('');
        }
      });
    }
  });
};

const SUPPORTED_URL_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'sms:', 'tel:']);

export function sanitizeUrl(url: string): string {
  try {
    const parsedUrl = new URL(url);
    // eslint-disable-next-line no-script-url
    if (!SUPPORTED_URL_PROTOCOLS.has(parsedUrl.protocol)) {
      return 'about:blank';
    }
  } catch {
    return url;
  }
  return url;
}

// Source: https://stackoverflow.com/a/8234912/2013580
const urlRegExp = new RegExp(
  /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+|(?:www.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[\w]*))?)/
);
export function validateUrl(url: string): boolean {
  // TODO Fix UI for link insertion; it should never default to an invalid URL such as https://.
  // Maybe show a dialog where they user can type the URL before inserting it.
  return url === 'https://' || urlRegExp.test(url);
}

const VERTICAL_GAP = 10;
const HORIZONTAL_OFFSET = 5;

export function setFloatingElemPositionForLinkEditor(
  targetRect: DOMRect | null,
  floatingElem: HTMLElement,
  anchorElem: HTMLElement,
  verticalGap: number = VERTICAL_GAP,
  horizontalOffset: number = HORIZONTAL_OFFSET
): void {
  const scrollerElem = anchorElem.parentElement;

  if (targetRect === null || !scrollerElem) {
    floatingElem.style.opacity = '0';
    floatingElem.style.transform = 'translate(-10000px, -10000px)';
    return;
  }

  const floatingElemRect = floatingElem.getBoundingClientRect();
  const anchorElementRect = anchorElem.getBoundingClientRect();
  const editorScrollerRect = scrollerElem.getBoundingClientRect();

  let top = targetRect.top - verticalGap;
  let left = targetRect.left - horizontalOffset;

  if (top < editorScrollerRect.top) {
    top += floatingElemRect.height + targetRect.height + verticalGap * 2;
  }

  if (left + floatingElemRect.width > editorScrollerRect.right) {
    left = editorScrollerRect.right - floatingElemRect.width - horizontalOffset;
  }

  top -= anchorElementRect.top;
  left -= anchorElementRect.left;

  floatingElem.style.opacity = '1';
  floatingElem.style.transform = `translate(${left}px, ${top}px)`;
}

export function transformLexicalToSlides(lexicalJson) {
  if (!lexicalJson) return [];
  const groupedNodes = groupNodes(JSON.parse(lexicalJson).root.children);
  return groupedNodes.map((group, index) => createSlide(group, index));
}

function createRun(node, linkUrl = null) {
  return {
    type: 'run',
    content: node.text,
    bold: Boolean(node.format & 1),
    italic: Boolean(node.format & 2),
    underline: Boolean(node.format & 8),
    strikethrough: Boolean(node.format & 4),
    superscript: Boolean(node.format & 64),
    subscript: Boolean(node.format & 32),
    color: parseStyle(node.style, 'color'),
    backgroundColor: parseStyle(node.style, 'background-color'),
    linkUrl: linkUrl,
  };
}

function createRichTextRuns(node) {
  const runs = [];

  if (node.type === 'text') {
    runs.push(createRun(node));
  } else if (node.type === 'linebreak') {
    runs.push({
      type: 'run',
      content: '\n',
      bold: false,
      italic: false,
      underline: false,
      strikethrough: false,
      superscript: false,
      subscript: false,
      color: null,
      backgroundColor: null,
      linkUrl: null,
    });
  } else if (node.type === 'link' && node.children) {
    node.children.forEach((child) => runs.push(createRun(child, node.url)));
  } else if (node.type === 'equation') {
    runs.push({
      type: 'run',
      content: `$${node.equation}$`,
      bold: false,
      italic: false,
      underline: false,
      strikethrough: false,
      superscript: false,
      subscript: false,
      color: null,
      backgroundColor: null,
      linkUrl: null,
    });
  }

  return runs;
}

function isHeading(node) {
  return node.type === 'heading';
}

function isEmptyTextNode(node) {
  return node.type === 'paragraph' && (!node.children || node.children.length === 0);
}

function groupNodes(lexicalNodes) {
  const groups = [];
  let currentGroup = [];

  lexicalNodes.forEach((node) => {
    if (isHeading(node)) {
      if (currentGroup.length > 0) {
        groups.push(currentGroup);
      }
      currentGroup = [node];
    } else if (isEmptyTextNode(node)) {
      if (currentGroup.length > 0) {
        groups.push(currentGroup);
      }
      currentGroup = [];
    } else {
      currentGroup.push(node);
    }
  });

  // Add last group if it has content
  if (currentGroup.length > 0) {
    groups.push(currentGroup);
  }

  return groups;
}

const BLOCK_TYPES = {
  quote: 'quote',
};

const groupRuns = (children) => {
  const result = [];
  let currentGroup = [];

  children.forEach((item, index) => {
    if (item.type === 'image' || (item.type === 'equation' && !item.inline)) {
      if (currentGroup.length > 0) result.push(currentGroup);
      result.push(item);
      currentGroup = [];
    } else {
      currentGroup.push(item);
    }

    if (index === children.length - 1) {
      if (currentGroup.length > 0) result.push(currentGroup);
    }
  });

  return result;
};

function createSlide(group, order) {
  const LIST_TYPES = {
    bullet: 'bulletedList',
    number: 'numberedList',
    check: 'checklist',
  };
  let elements = [];
  group.map((node) => {
    if (['heading', 'paragraph', 'quote'].includes(node.type)) {
      const groupedRuns = groupRuns(node.children);
      groupedRuns.forEach((group) => {
        if (Array.isArray(group)) {
          elements.push({
            type: BLOCK_TYPES[node.type] || 'paragraph',
            paragraphStyle: getParagraphStyle(node.type, node.tag),
            richText: group.flatMap(createRichTextRuns),
            order: 0,
          });
        } else {
          if (group.type === 'image') {
            elements.push({
              type: 'image',
              url: group.src,
              alt: group.alt,
            });
          } else if (group.type === 'equation') {
            elements.push({
              type: 'equation',
              paragraphStyle: 'normal',
              expression: group.equation,
            });
          }
        }
      });
    } else if (node.type === 'list') {
      elements.push({
        type: LIST_TYPES[node.listType],
        children: node.children.map((listItem) => ({
          type: LIST_TYPES[node.listType] + 'Item',
          paragraphStyle: 'normal',
          richText: listItem.children.flatMap(createRichTextRuns),
          checked: listItem.checked,
        })),
      });
    } else if (node.type === 'code') {
      const codeContent = node.children
        .filter((child) => child.type === 'code-highlight' || child.type === 'linebreak')
        .map((child) => (child.type === 'linebreak' ? '\n' : child.text))
        .join('');

      elements.push({
        type: 'code',
        paragraphStyle: 'normal',
        richText: [
          {
            type: 'run',
            content: codeContent,
            bold: false,
            italic: false,
            strikethrough: false,
            underline: false,
            color: null,
            code: true,
            backgroundColor: null,
            linkUrl: null,
            superscript: false,
            subscript: false,
          },
        ],
      });
    } else if (node.type === 'table') {
      elements.push({
        type: 'notionTable',
        children: node.children.map((row) => ({
          type: 'row',
          children: row.children.map((cell) => ({
            type: 'cell',
            colspan: 1,
            rowspan: 1,
            children: cell.children.map((block) => ({
              type: 'paragraph',
              paragraphStyle: 'normal',
              horizontalAlignment: 'LEFT',
              richText: block.children.flatMap(createRichTextRuns),
            })),
          })),
        })),
      });
    } else if (node.type === 'video') {
      elements.push({
        type: 'video',
        url: node.videoUrl,
      });
    }
  });

  return {
    order: order,
    elements: elements.filter(Boolean), // Filter out any undefined elements
  };
}

function getParagraphStyle(type, tag) {
  const mapping = {
    heading: { h1: 'heading1', h2: 'heading2', h3: 'heading3' }[tag],
  };
  return mapping[type] || 'normal';
}

function parseStyle(style, key) {
  if (!style) return null;
  const styles = style.split(';');
  for (const s of styles) {
    const [prop, value] = s.split(':');
    if (prop.trim() === key) return value.trim();
  }
  return null;
}

export function useDebounce<T extends (...args: never[]) => void>(
  fn: T,
  ms: number,
  maxWait?: number
) {
  const funcRef = useRef<T | null>(null);
  funcRef.current = fn;

  return useMemo(
    () =>
      debounce(
        (...args: Parameters<T>) => {
          if (funcRef.current) {
            funcRef.current(...args);
          }
        },
        ms,
        { maxWait }
      ),
    [ms, maxWait]
  );
}

export function base64ToFile(dataUri, fileName) {
  const [header, base64Data] = dataUri.split(',');
  const mimeType = header.match(/:(.*?);/)[1];

  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);

  const blob = new Blob([byteArray], { type: mimeType });
  return new File([blob], fileName, { type: mimeType });
}

export function isValidUrl(string) {
  try {
    // Try to construct a new URL object
    new URL(string);
    return true;
  } catch (_) {
    // If an error is thrown, it's not a valid URL
    return false;
  }
}
